import { useMemo, useState } from "react"
import { useTranslation } from "@/i18n"
import { useProfileSearch } from "@/hooks/useProfileSearch"
import { useProfiles } from "@/hooks/useProfiles"
import type { FollowingMode } from "@/nostr/api/filters/authorFilter"
import { sourceOptions } from "@/nostr/api/filters/sourceFilter"
import { shortNpub, toPubkey } from "@/utils/nip19"
import { simplifyUrl } from "@/utils/url"
import { FilterChipGroup, FilterField, FilterSection, type FilterCategoryProps } from "./FilterSection"

const FOLLOWING_LABELS: Record<FollowingMode, string> = {
  everyone: "Everyone",
  following: "Following only",
  "non-following": "Non-following users",
}

export interface PeopleSourceFiltersProps extends FilterCategoryProps {
  /** The reader's follow list, offered as quick picks. */
  following?: string[]
  /** Relays of the current list, offered by the source filter. */
  sources?: string[]
  showFollowing?: boolean
  showSources?: boolean
}

/**
 * PEOPLE & SOURCES: author include / exclude, following status and relays.
 *
 * The author picker searches the profiles the client already cached and also
 * accepts a raw `npub` / hex key, so an author can be added before their profile
 * has ever been fetched.
 */
export function PeopleSourceFilters({
  value,
  onChange,
  onReset,
  following = [],
  sources = [],
  showFollowing = true,
  showSources = false,
}: PeopleSourceFiltersProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState("")
  const results = useProfileSearch(term, 6)
  const followingPreview = useMemo(() => following.slice(0, 12), [following])
  const profiles = useProfiles([...value.authors, ...followingPreview])
  const relayOptions = useMemo(() => sourceOptions(sources), [sources])
  const typedKey = useMemo(() => toPubkey(term.trim()), [term])

  const nameOf = (pubkey: string) => {
    const profile = profiles.get(pubkey)
    return profile?.displayName || profile?.name || shortNpub(pubkey)
  }

  const toggleAuthor = (pubkey: string) => {
    const next = value.authors.includes(pubkey)
      ? value.authors.filter((item) => item !== pubkey)
      : [...value.authors, pubkey]
    onChange({ authors: next })
    setTerm("")
  }

  const toggleSource = (relay: string) => {
    const next = value.relays.includes(relay)
      ? value.relays.filter((item) => item !== relay)
      : [...value.relays, relay]
    onChange({ relays: next })
  }

  return (
    <FilterSection id="filter-section-people" title={t("People & sources")} onReset={onReset}>
      <FilterField label={t("Author filter mode")}>
        <select
          value={value.authorMode}
          onChange={(event) => onChange({ authorMode: event.target.value as "include" | "exclude" })}
          aria-label={t("Author filter mode")}
        >
          <option value="include">{t("Only these authors")}</option>
          <option value="exclude">{t("Hide these authors")}</option>
        </select>
      </FilterField>

      <FilterField label={t("Authors")} hint={t("Search by name, username, npub or public key.")}>
        <div className="relay-filter filter-search">
          <input
            type="text"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t("Search people")}
            aria-label={t("Search people")}
            spellCheck={false}
          />
        </div>
        {term.trim() && (
          <ul className="filter-suggestion-list">
            {typedKey && !value.authors.includes(typedKey) && (
              <li>
                <button type="button" className="filter-suggestion" onClick={() => toggleAuthor(typedKey)}>
                  {t("Use this public key")} · {shortNpub(typedKey)}
                </button>
              </li>
            )}
            {results.map((profile) => (
              <li key={profile.pubkey}>
                <button
                  type="button"
                  className="filter-suggestion"
                  onClick={() => toggleAuthor(profile.pubkey)}
                >
                  {profile.displayName || profile.name || shortNpub(profile.pubkey)}
                  {profile.nip05 && <span className="muted small"> · {profile.nip05}</span>}
                </button>
              </li>
            ))}
            {!typedKey && results.length === 0 && (
              <li className="muted small">{t("No cached profile matches. Paste an npub or public key.")}</li>
            )}
          </ul>
        )}
        {value.authors.length > 0 && (
          <div className="filter-chip-group">
            {value.authors.map((pubkey) => (
              <button
                key={pubkey}
                type="button"
                className="chip filter-chip active"
                onClick={() => toggleAuthor(pubkey)}
                aria-label={t("Remove {name}", { name: nameOf(pubkey) })}
              >
                {nameOf(pubkey)} ✕
              </button>
            ))}
          </div>
        )}
      </FilterField>

      {showFollowing && followingPreview.length > 0 && (
        <FilterField label={t("From your follow list")}>
          <div className="filter-chip-group">
            {followingPreview.map((pubkey) => {
              const active = value.authors.includes(pubkey)
              return (
                <button
                  key={pubkey}
                  type="button"
                  className={`chip filter-chip ${active ? "active" : ""}`.trim()}
                  aria-pressed={active}
                  onClick={() => toggleAuthor(pubkey)}
                >
                  {nameOf(pubkey)}
                </button>
              )
            })}
          </div>
        </FilterField>
      )}

      {showFollowing && (
        <FilterField label={t("Following status")}>
          <select
            value={value.following}
            onChange={(event) => onChange({ following: event.target.value as FollowingMode })}
            aria-label={t("Following status")}
          >
            {(Object.keys(FOLLOWING_LABELS) as FollowingMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {t(FOLLOWING_LABELS[mode])}
              </option>
            ))}
          </select>
        </FilterField>
      )}

      {showSources && relayOptions.length > 0 && (
        <FilterField label={t("Sources")} hint={t("Only these relays are queried for this list.")}>
          <FilterChipGroup
            options={relayOptions.map((relay) => ({ value: relay, label: simplifyUrl(relay) }))}
            selected={value.relays}
            onToggle={toggleSource}
            ariaLabel={t("Sources")}
          />
        </FilterField>
      )}
    </FilterSection>
  )
}
